"use client";

import React from "react";
import { useTheme } from "@/context/ThemeContext";
import { FarminstaIntroLayout } from "@/app/home/landing-page";
import { HeroSection } from "@/components/HeroSection";
import { ScrollingLogosComponent } from "@/app/home/scrolling-logos";
import AnimatedSection from "@/app/home/animated-section";
import { PandoforceLayout } from "@/app/pandoforce/home/layout";
import {
  PfCampaignHubFeatures,
  PfFeaturesSection,
  PfHeroSection,
  PfOperationsSection,
  PfTestimonials,
} from "@/app/pandoforce/page";

import { StatsSection } from "@/app/page";
import { Button } from "@/components/ui/button";
import {
  ArrowUpRight,
  BarChart2,
  Check,
  DollarSign,
  Filter,
  Rocket,
  Star,
  Trophy,
} from "lucide-react";
import image2 from "@/public/images/WebsiteImages/home/2.svg";
import image3 from "@/public/images/WebsiteImages/home/3.svg";
import udayImg from "@/public/images/WebsiteImages/home/UdayKumar.png";
import purna from "@/public/images/WebsiteImages/home/PurnaChandraRao.jpg";
import arun from "@/public/images/WebsiteImages/home/ArunSoni.jpeg";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import Image from "next/image";
import { useDemoModal } from "@/hooks/useDemoModal";
import SVGRender from "@/assets/svgs/TracData";
import img3 from '../public/images/WebsiteImages/fcm/3.png'


interface FeatureProps {
  icon: React.ElementType;
  title: string;
  description: string;
}

export const FeaturesSection = () => (
  <section className="py-12 px-6 bg-white">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-4xl font-bold mb-4 text-gray-900 text-center">
        One platform. Every layer of your season.
      </h2>
      <div className="flex flex-col items-center justify-center  mb-12">
        <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto">
          Built over a decade with India's leading agri-input brands.
        </p>
        <p className="text-xl text-gray-600  text-center max-w-3xl mx-auto">
           Every rupee of season spend works as one engine.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-[rgba(17,_17,_26,_0.1)_0px_0px_16px]">
          <h3 className="text-xl font-bold mb-4 text-gray-900">
            Make Your Field
            <br />
             Force Measurable
          </h3>
          <p className="text-gray-600">
            Track field officer attendance, activities, farmer visits, and demo plots in real time. Convert daily field motion into reported outcomes.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-[rgba(17,_17,_26,_0.1)_0px_0px_16px]">
          <h3 className="text-xl font-bold mb-4 text-gray-900">
            Own Your Farmer Relationship
           
         
          </h3>
          <p className="text-gray-600">
           Build a proprietary farmer database with crop, demo, and purchase history. Run targeted campaigns, capture sales commitments, and close the loop from intent to delivery.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-[rgba(17,_17,_26,_0.1)_0px_0px_16px]">
          <h3 className="text-xl font-bold mb-4 text-gray-900">
           Convert Engagement Into Revenue
            <br />
         
          </h3>
          <p className="text-gray-600">
            Engage farmers through vernacular video, WhatsApp, and loyalty points. Generate qualified leads for your field officers and turn brand affinity into repeat purchase.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export const OperationsSection = () => (
  <section className="py-12 px-6 bg-[#ebf2f0]">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12">
      <div className="md:w-1/2">
        <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-gray-900 leading-tight">
         Run Your Field Force Like an Operating System
        </h2>
        <div>
          <p className="mb-4">
            Every field officer visit, every demo plot, every dealer call — captured, GPS-tagged, and rolled into a single performance view. Stop reconstructing what happened in the field last week. Start managing it as it happens.{" "}
          </p>
        </div>
        <ul className="space-y-6">
          {[
            {
              title: "Real-Time Activity Tracking ",
              description:
                "Attendance, visits, group meetings, field days, and demo plots logged from the mobile app with GPS coordinates and timestamps. Daily activity reports delivered before 8 PM. ",
            },
            {
              title: "Demo Lifecycle ",
              description:
                "Track every demo plot from sowing to harvest. Five stages, drop-off analytics, and yield outcomes per variety, per market — the commercial proof of product performance. ",
            },
            {
              title: "Liquidation Tracking",
              description:
                "Move beyond sell-in. Track sell-out from retailer to farmer with stock updates, sales actuals, and channel performance scoreboards.",
            },
          ].map((item, index) => (
            <li key={index} className="flex items-start">
              <div className="bg-primary  rounded-full p-1.5 mr-4 mt-1 shrink-0">
                <Check className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1 text-gray-900">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:w-1/2 flex justify-center">
        <div className="relative w-full max-w-md">
        <SVGRender/>
        </div>
      </div>
    </div>
  </section>
);

export const BrandVisibilitySection = () => (
  <section className="py-12 px-6 bg-white">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row-reverse items-center gap-12">
      <div className="md:w-1/2">
        <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900 leading-tight">
          Convert Demand Into Liquidation
        </h2>
        <div>
          <p className="mt-2 mb-4">
            Most agri brands stitch together five different channels and still can't trace a sale back to spend. Farminsta Marketing Pod is a fully managed engagement — one dedicated women voice agent, five integrated channels, your existing field officers made measurable, and your retailers closed into the loop. Built around your crop season. Priced for accountability.{" "}
          </p>
        </div>
        <ul className="space-y-6">
          {[
            {
              title: "Nurture Your Pipeline, Generate New Leads",
              description:
                "We work your existing FO leads harder and bring fresh farmers into the funnel through influencer content, performance marketing, WhatsApp, SMS, and AI-driven outbound — then route qualified leads back to your FOs for conversion. ",
            },
            {
              title: "Quality-Control Your Demand Activity ",
              description:
                "Daily validation of FO field activity. Market coverage tracking. Target progression. Insight reports built for sales managers — actionable scorecards on where the season is winning and where it's slipping.",
            },
            {
              title: "Close the Loop at the Retailer Counter",
              description:
                "Our women agents engage retailers across your priority markets — supporting product placement, tracking stock movement, gathering collection signals, and surfacing liquidation roadblocks before they become season-end inventory.",
            },
          ].map((item, index) => (
            <li key={index} className="flex items-start">
              <div className="bg-primary  rounded-full p-1.5 mr-4 mt-1 shrink-0">
                <Check className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1 text-gray-900">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:w-1/2 flex justify-center">
        <div className="relative">
          <Image
            src={img3}
            alt="Data tracking and reporting illustration"
            className="w-full h-[500px] object-cover "
          />
        </div>
      </div>
    </div>
  </section>
);

export const Feature: React.FC<FeatureProps> = ({
  icon: Icon,
  title,
  description,
}) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-green-600" />
    </div>
    <h3 className="text-xl font-semibold text-gray-900 mb-3 leading-tight">
      {title}
    </h3>
    <p className="text-gray-600 leading-relaxed">{description}</p>
  </div>
);

export const CampaignHubFeatures: React.FC = () => {
  const features: FeatureProps[] = [
    {
      icon: ArrowUpRight,
      title: "Reach villages before your FOs walk them",
      description:
        "Warm new districts with digital and voice campaigns, then send your FOs into known territory. Expand coverage without expanding headcount.",
    },
    {
      icon: Rocket,
      title: "Put your brand in front of farmers daily",
      description:
        "Short-form videos, reels, and influencer content built for rural audiences — running across the markets where your FOs are active.",
    },
    {
      icon: DollarSign,
      title: "Replace expensive activities with measurable ones",
      description:
        "Shift spend from one-off van campaigns and demo days to always-on digital and voice engagement — trackable per district, per SKU, per season.",
    },
    {
      icon: Filter,
      title: "One farmer record across every touchpoint",
      description:
        "FO visits, voice calls, WhatsApp replies, demo attendance, and reward redemptions — all on one farmer timeline your team can act on.",
    },
    {
      icon: BarChart2,
      title: "See the season as it moves",
      description:
        "District-level engagement signals and FO activity scorecards show where the season is winning and where it's slipping — while you can still course-correct.",
    },
    {
      icon: Trophy,
      title: "Reward the farmers who stay",
      description:
        "Built-in points engine you control.",
    },
  ];

  return (
    <section className="px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-[#ebf2f0] rounded-[40px] p-12 md:p-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-12 text-center">
             Run your season on Farminsta
          </h2>

          <div className="grid md:grid-cols-2 gap-x-16 gap-y-12">
            {features.map((feature, index) => (
              <div key={index} className="flex gap-4">
                <div className="mt-1">
                  <feature.icon className="w-5 h-5 text-gray-900" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};


    let handleDemo = () => {
        window.open("https://outlook.office.com/bookwithme/user/1561a76e4da64eb5b98221915f4730ad@farmreach.org?anonymous&ep=pcard")
    }

export const DemoCallToActionSection = () => {
   const { openModal, DemoModal } = useDemoModal();
  
      const handleDemo = () => {
          openModal();
      }

  return(
    <>
  <section className="px-4 pb-12 sm:px-6 lg:px-8 bg-white">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
        Run your next season with Farminsta.
      </h2>
      <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
        Twenty minutes is all we need to show you the platform live — with your crops, your geography, and your team in mind.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
        <Button
          size="lg"
          className="bg-primary hover:bg-secondary text-white px-8 py-6 text-lg rounded-sm font-bold transition duration-300 ease-in-out transform hover:scale-105"
          onClick={handleDemo}
        >
          Book a Demo
        </Button>
        {/* <Button 
          size="lg"
          variant="outline"
          className="border-2 border-[#FF6B6B] text-[#FF6B6B] hover:bg-[#FF6B6B] hover:text-white px-8 py-6 text-lg"
          onClick={() => window.location.href = '/marketing-pod'}
        >
          Explore Marketing Pod
        </Button> */}
      </div>
    </div>
  </section>
  <DemoModal />
  </>
  )
};

export const Testimonials = () => {
  const testimonials = [
    {
      rating: 5,
      text: "Farminsta has transformed how I manage field operations. The digital platform has made reporting easier, communication more effective, and my overall workflow much more efficient",
      name: "Uday Kumar",
      role: "Field Officer- Advanta",
      initials: "UK",
      image: udayImg,
    },
    {
      rating: 5,
      text: "Farminsta has been a game-changer for my farm. The advisory reels are invaluable, and the ability to connect with knowledgeable field officer.It's like having a support system right in my pocket!\"",
      name: "Purna Chandra Rao",
      role: "Farmer- Guntur, Andhrapradesh",
      initials: "PC",
      image: purna,
    },
    {
      rating: 5,
      text: "Farminsta has transformed the way we approach agri-marketing. The real-time market data access have given seasonal insights and comparative analysis. Our campaigns are now more targeted.",
      name: "Arun soni",
      role: "ZM- Savannah Seeds",
      initials: "AS",
      image: arun,
    },
  ];

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Check our clients feedback!
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-[rgba(17,_17,_26,_0.1)_0px_0px_16px]"
            >
              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 fill-yellow-400 text-yellow-400"
                  />
                ))}
              </div>

              <p className="text-gray-900 mb-6 leading-relaxed">
                {testimonial.text}
              </p>

              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 relative">
                  {testimonial.image ? (
                    <Image
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="rounded-full"
                    />
                  ) : (
                    <AvatarFallback className="bg-gray-100 text-gray-600">
                      {testimonial.initials}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {testimonial.name}
                  </h3>
                  <p className="text-sm text-gray-500">{testimonial.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export function FIHomeContent() {
  const { theme } = useTheme();

  // Client-side redirect for Growthbook domain
  React.useEffect(() => {
    if (theme.name.includes("Shriram") && typeof window !== "undefined") {
      window.location.href = "/auth/login";
    }
  }, [theme]);

  const farminstaContent = (
    <FarminstaIntroLayout currentPage="Home">
      <HeroSection />
      <ScrollingLogosComponent />
      <AnimatedSection>
        <FeaturesSection />
      </AnimatedSection>
      <AnimatedSection>
        <OperationsSection />
      </AnimatedSection>
      <AnimatedSection>
        <BrandVisibilitySection />
      </AnimatedSection>
      <AnimatedSection>
        <CampaignHubFeatures />
      </AnimatedSection>
      <AnimatedSection>
        <Testimonials />
      </AnimatedSection>
      {/* <AnimatedSection>
        <StatsSection />
      </AnimatedSection> */}
      <AnimatedSection>
        <DemoCallToActionSection />
      </AnimatedSection>
    </FarminstaIntroLayout>
  );

  const pandoforceContent = (
    <PandoforceLayout currentPage="Home">
      <PfHeroSection />
      <PfFeaturesSection />
      <AnimatedSection>
        <PfOperationsSection />
      </AnimatedSection>
      <AnimatedSection>
        <PfCampaignHubFeatures />
      </AnimatedSection>
      {/* <AnimatedSection>
                <PfTestimonials/>
            </AnimatedSection> */}
    </PandoforceLayout>
  );

  if (
    theme.name.includes("Farminsta") ||
    theme.name.includes("localhost") ||
    theme.name.includes("Shriram")
  ) {
    return farminstaContent;
  } else if (theme.name.includes("Pandoforce")) {
    return pandoforceContent;
  } else {
    return <div>Unknown domain: {theme.name}</div>;
  }
}
